import { 
  choosing_supplement as qpt_choosing_supplement, 
  supplement_bacteria_map as qpt_supplement_bacteria_map 
} from '../configs/supplement/qpt_supplement';
import {
  choosing_supplement_9P,
  supplement_bacteria_map_9P,
  choosing_supplement_9P_old,
  supplement_bacteria_map_9P_old
} from '../configs/supplement/9P_supplement';
import { 
  choosing_supplement_AMG, 
  supplement_bacteria_map_AMG 
} from '../configs/supplement/AMG_supplement';
import { 
  choosing_supplement_toxin, 
  supplement_bacteria_map_toxin 
} from '../configs/supplement/toxin_supplement';
import { 
  choosing_supplement_oms, 
  supplement_bacteria_map_oms 
} from '../configs/supplement/oms_supplement';
import { 
  choosing_supplement_bwc, 
  supplement_bacteria_map_bwc,
  choosing_supplement_bwcdiamond,
  supplement_bacteria_map_bwcdiamond
} from '../configs/supplement/bwc_supplement';
import { bacteriaMetaConfig } from '../configs/calculate_ctvalue';
import { evaluateBacteriaCondition } from '../utils/bacteriaEvaluator';
import { getPetCareProRecommendedSupplementList } from '../configs/biopet_essential/calculate_biopet_essential_supplement';

// Panels that support the "New formula / Original" toggle. When 'new' is active
// they all share the same 13 single-strain SKU set (originally built for 9P);
// when 'old' each uses its own original supplement map/config.
const NEW_FORMULA_PANELS = [
  '9P',
  'QPT',
  'Allergy and Skin_kids',
  'bwcallergy', 'BWC-A',
  'bwccore', 'BWC-C',
  'bwcdiamond', 'BWC-D'
];
export const panelSupportsNewFormula = (reportMode) => NEW_FORMULA_PANELS.includes(reportMode);

// Which supplement formula the calculation uses: 'new' (single-strain SKUs) or
// 'old' (each panel's original blend). Single global user choice per session,
// set from the CSV Import Preview toggle via set9PSupplementVersion(), so it does
// not have to be threaded through every getRecommendedSupplements call site.
let active9PSupplementVersion = 'new';
export const set9PSupplementVersion = (version) => {
  active9PSupplementVersion = version === 'old' ? 'old' : 'new';
};
export const get9PSupplementVersion = () => active9PSupplementVersion;

// Resolve the config (deficiency species) + coverage map for a panel, honouring
// the active version. New formula → shared 9P SKUs for every supported panel;
// otherwise each panel's own original data.
const resolveSupplementData = (reportMode) => {
  if (active9PSupplementVersion === 'new' && panelSupportsNewFormula(reportMode)) {
    return { config: choosing_supplement_9P[0], supplementMap: supplement_bacteria_map_9P };
  }
  if (reportMode === '9P') {
    return { config: choosing_supplement_9P_old[0], supplementMap: supplement_bacteria_map_9P_old };
  }
  if (reportMode === 'oms') {
    return { config: choosing_supplement_oms[0], supplementMap: supplement_bacteria_map_oms };
  }
  if (reportMode === 'Allergy and Skin') {
    return { config: choosing_supplement_AMG[0], supplementMap: supplement_bacteria_map_AMG };
  }
  if (reportMode === 'Allergy and Skin_kids') {
    return { config: qpt_choosing_supplement[0], supplementMap: qpt_supplement_bacteria_map };
  }
  if (reportMode === 'Toxin') {
    return { config: choosing_supplement_toxin[0], supplementMap: supplement_bacteria_map_toxin };
  }
  if (['bwcallergy', 'bwccore', 'BWC-A', 'BWC-C'].includes(reportMode)) {
    return { config: choosing_supplement_bwc[0], supplementMap: supplement_bacteria_map_bwc };
  }
  if (reportMode === 'bwcdiamond' || reportMode === 'BWC-D') {
    return { config: choosing_supplement_bwcdiamond[0], supplementMap: supplement_bacteria_map_bwcdiamond };
  }
  return { config: qpt_choosing_supplement[0], supplementMap: qpt_supplement_bacteria_map };
};

// Function to calculate the label for a species based on its value
export function calculateSpeciesLabel(species, value, formData) {
  const config = bacteriaMetaConfig[species];
  if (!config || !config.rangeMapping) {
    return 'Normal'; // Default if no config found
  }
  
  for (const range of config.rangeMapping) {
    if (evaluateBacteriaCondition(range.condition, value, formData, species)) {
      return range.label;
    }
  }
  
  return 'Normal'; // Default if no condition matches
}

// Function to get all available supplements for a specific panel
export function getAllPanelSupplements(reportMode) {
  if (reportMode === 'pet-care-pro') {
    return ['PetReno', 'PetGard', 'PetFlow'];
  }

  return Object.keys(resolveSupplementData(reportMode).supplementMap);
}

export function getRecommendedSupplements(formData, reportMode, stock, excludedSupplements = [], stockThreshold = 800, durationExclusions = {}, considerStockLevels = true, quartileGridBySpecies = null) {
  if (reportMode === 'pet-care-pro') {
    const recommended = getPetCareProRecommendedSupplementList(formData, quartileGridBySpecies);
    const supplements = recommended.filter((name) => !excludedSupplements.includes(name));
    return {
      supplements,
      coverage: {
        covered: supplements.length,
        total: Math.max(recommended.length, 1),
        percentage: supplements.length ? 100 : 100,
        uncoveredSpecies: []
      }
    };
  }

  const { config, supplementMap } = resolveSupplementData(reportMode);

  // Get deficient species
  const missingSpecies = config.info
    .filter(({ sp, less }) => {
      const value = parseFloat(formData[sp]) || 0;
      const calculatedLabel = calculateSpeciesLabel(sp, value, formData);
      const isDeficient = Array.isArray(less) ? less.includes(calculatedLabel) : false;
      return isDeficient;
    })
    .map(({ sp }) => sp);

  console.log('=== SPECIES CALCULATION DEBUG ===');
  console.log('Form data:', formData);
  console.log('Config info:', config.info);
  console.log('Missing species (deficient):', missingSpecies);
  console.log('Total deficient species:', missingSpecies.length);

  // If no deficient species, return empty array
  if (missingSpecies.length === 0) {
    console.log('No deficient species found - returning empty supplements array');
    return { supplements: [], coverage: { covered: 0, total: 0, percentage: 100, uncoveredSpecies: [] } };
  }

  // New-formula SKUs are not tracked in the `stock` table (no columns), so a
  // stock-level filter would exclude every one of them and return nothing.
  // Skip stock filtering while the new formula is active.
  const skipStockFilter = active9PSupplementVersion === 'new' && panelSupportsNewFormula(reportMode);

  // Create a map of supplements to their covered species with scores
  // EXCLUDE LOW STOCK SUPPLEMENTS FROM THE BEGINNING (only if considerStockLevels is true)
  const supplementCoverage = {};
  for (const [supplement, bacteriaMap] of Object.entries(supplementMap)) {
    // Skip supplements with low stock (less than stockThreshold) or excluded supplements
    const currentStock = stock[supplement] || 0;
    const isExcludedByStock = considerStockLevels && !skipStockFilter && currentStock < stockThreshold;
    const isExcludedByFDA = excludedSupplements.includes(supplement);
    
    if (isExcludedByStock || isExcludedByFDA) {
      // console.log(`Excluding ${supplement} from calculation - Stock: ${currentStock}, Threshold: ${stockThreshold}, FDA Excluded: ${isExcludedByFDA}, ConsiderStockLevels: ${considerStockLevels}`);
      continue;
    }
    
    supplementCoverage[supplement] = {
      species: new Set(),
      totalScore: 0,
      coverage: {}
    };
    
    for (const [species, score] of Object.entries(bacteriaMap)) {
      if (missingSpecies.includes(species)) {
        supplementCoverage[supplement].species.add(species);
        supplementCoverage[supplement].coverage[species] = score;
        supplementCoverage[supplement].totalScore += score;
      }
    }
  }

  console.log(`=== AVAILABLE SUPPLEMENTS (Stock >= ${stockThreshold}) ===`);
  console.log('Available supplements:', Object.keys(supplementCoverage));
  console.log('Excluded supplements (low stock or FDA):', excludedSupplements);
  console.log('Stock threshold:', stockThreshold);
  console.log('Consider stock levels:', considerStockLevels);

  // If there are excluded supplements, we need to find optimal replacements
  if (excludedSupplements.length > 0) {
    console.log('Using replacement supplement logic due to exclusions');
    return findOptimalReplacementSupplements(supplementCoverage, missingSpecies, excludedSupplements, stock);
  }

  // Original algorithm for when no supplements are excluded
  console.log('Using standard supplement selection logic');
  const result = findOptimalSupplements(supplementCoverage, missingSpecies);
  console.log('Final supplement selection result:', result);
  return result;
}

// Function to find optimal supplements when no FDA supplements are excluded
function findOptimalSupplements(supplementCoverage, missingSpecies) {
  // Create a map of species to supplements that cover them
  const speciesToSupplements = {};
  missingSpecies.forEach(species => {
    speciesToSupplements[species] = [];
    Object.entries(supplementCoverage).forEach(([supplement, coverage]) => {
      if (coverage.species.has(species)) {
        speciesToSupplements[species].push(supplement);
      }
    });
  });
  
  // Find the minimal set of supplements that covers all species
  const minimalSupplements = findMinimalCoverage(speciesToSupplements, missingSpecies, supplementCoverage);
  
  // Calculate coverage statistics
  const totalCoveredSpecies = new Set();
  minimalSupplements.forEach(supplement => {
    const coverage = supplementCoverage[supplement];
    if (coverage) {
      coverage.species.forEach(species => {
        if (missingSpecies.includes(species)) {
          totalCoveredSpecies.add(species);
        }
      });
    }
  });
  
  const uncoveredSpecies = missingSpecies.filter(species => !totalCoveredSpecies.has(species));
  const coveragePercentage = Math.round((totalCoveredSpecies.size / missingSpecies.length) * 100);
  
  // console.log('=== SUPPLEMENT SELECTION RESULTS (No FDA Exclusions) ===');
  // console.log('Selected supplements:', minimalSupplements);
  // console.log('Coverage:', `${totalCoveredSpecies.size}/${missingSpecies.length} species (${coveragePercentage}%)`);
  // console.log('Lacking species:', uncoveredSpecies);
  
  // Add detailed coverage information for debugging
  // console.log('=== DETAILED COVERAGE ANALYSIS ===');
  minimalSupplements.forEach(supplement => {
    const coverage = supplementCoverage[supplement];
    if (coverage) {
      // console.log(`${supplement}:`, {
      //   species: Array.from(coverage.species),
      //   coverage: coverage.coverage,
      //   totalScore: coverage.totalScore
      // });
    }
  });
  
  return { 
    supplements: minimalSupplements, 
    coverage: { 
      covered: totalCoveredSpecies.size, 
      total: missingSpecies.length, 
      percentage: coveragePercentage, 
      uncoveredSpecies: uncoveredSpecies 
    } 
  };
}

// Function to find minimal coverage using a more sophisticated algorithm
function findMinimalCoverage(speciesToSupplements, missingSpecies, supplementCoverage) {
  // Start with all supplements that cover any missing species
  const allCandidates = new Set();
  missingSpecies.forEach(species => {
    speciesToSupplements[species].forEach(supplement => {
      allCandidates.add(supplement);
    });
  });
  
  const candidatesArray = Array.from(allCandidates);
  
  // Try different combinations to find the minimal set
  let bestCombination = candidatesArray;
  let bestScore = candidatesArray.length;
  
  // Sort candidates by coverage efficiency (considering both species count and coverage amounts)
  const sortedCandidates = candidatesArray.sort((a, b) => {
    const aCoverage = supplementCoverage[a];
    const bCoverage = supplementCoverage[b];
    
    if (!aCoverage || !bCoverage) return 0;
    
    // Calculate weighted score based on both species count and coverage amounts
    const aSpeciesCount = aCoverage.species.size;
    const bSpeciesCount = bCoverage.species.size;
    
    // Calculate total coverage amount for missing species
    let aTotalAmount = 0;
    let bTotalAmount = 0;
    
    missingSpecies.forEach(species => {
      if (aCoverage.species.has(species)) {
        aTotalAmount += aCoverage.coverage[species] || 0;
      }
      if (bCoverage.species.has(species)) {
        bTotalAmount += bCoverage.coverage[species] || 0;
      }
    });
    
    // Prioritize supplements with higher coverage amounts for the same number of species
    if (aSpeciesCount === bSpeciesCount) {
      return bTotalAmount - aTotalAmount;
    }
    
    // If species count is different, still consider it but with less weight
    return bSpeciesCount - aSpeciesCount;
  });
  
  // Try greedy approach first
  const greedyResult = greedyMinimalCoverage(sortedCandidates, missingSpecies, supplementCoverage);
  if (greedyResult.length < bestScore) {
    bestCombination = greedyResult;
    bestScore = greedyResult.length;
  }
  
  // Try different starting points for greedy algorithm
  for (let i = 0; i < Math.min(5, sortedCandidates.length); i++) {
    const shuffledCandidates = [...sortedCandidates];
    // Move the i-th candidate to the front
    const temp = shuffledCandidates[0];
    shuffledCandidates[0] = shuffledCandidates[i];
    shuffledCandidates[i] = temp;
    
    const result = greedyMinimalCoverage(shuffledCandidates, missingSpecies, supplementCoverage);
    if (result.length < bestScore) {
      bestCombination = result;
      bestScore = result.length;
    }
  }
  
  return bestCombination;
}

// Greedy algorithm to find minimal coverage with priority based on coverage amounts
function greedyMinimalCoverage(candidates, missingSpecies, supplementCoverage) {
  const selectedSupplements = new Set();
  const coveredSpecies = new Set();
  
  // Create a map to track the best coverage for each species
  const speciesBestCoverage = {};
  missingSpecies.forEach(species => {
    speciesBestCoverage[species] = { supplement: null, amount: 0 };
  });
  
  // First pass: find the best supplement for each species based on coverage amount
  candidates.forEach(supplement => {
    const coverage = supplementCoverage[supplement];
    if (!coverage) return;
    
    coverage.species.forEach(species => {
      if (missingSpecies.includes(species)) {
        const amount = coverage.coverage[species] || 0;
        if (amount > speciesBestCoverage[species].amount) {
          speciesBestCoverage[species] = { supplement, amount };
        }
      }
    });
  });
  
  // Second pass: select supplements that provide the best coverage for uncovered species
  const remainingSpecies = new Set(missingSpecies);
  
  while (remainingSpecies.size > 0) {
    let bestSupplement = null;
    let bestScore = 0;
    let bestNewCoverage = [];
    
    // Find the supplement that provides the best coverage for remaining species
    candidates.forEach(supplement => {
      const coverage = supplementCoverage[supplement];
      if (!coverage) return;
      
      const newCoverage = Array.from(coverage.species)
        .filter(species => remainingSpecies.has(species));
      
      if (newCoverage.length > 0) {
        // Calculate score based on coverage amounts for remaining species
        let totalAmount = 0;
        newCoverage.forEach(species => {
          totalAmount += coverage.coverage[species] || 0;
        });
        
        // Prioritize supplements with higher coverage amounts
        if (totalAmount > bestScore) {
          bestScore = totalAmount;
          bestSupplement = supplement;
          bestNewCoverage = newCoverage;
        }
      }
    });
    
    if (bestSupplement) {
      selectedSupplements.add(bestSupplement);
      bestNewCoverage.forEach(species => {
        remainingSpecies.delete(species);
        coveredSpecies.add(species);
      });
    } else {
      // If no supplement can cover remaining species, break to avoid infinite loop
      break;
    }
  }
  
  // Optimize by removing redundant supplements
  const optimizedSupplements = removeRedundantSupplements(Array.from(selectedSupplements), missingSpecies, supplementCoverage);
  
  return optimizedSupplements;
}

// Function to remove redundant supplements
function removeRedundantSupplements(supplements, missingSpecies, supplementCoverage) {
  const finalSupplements = [...supplements];
  
  // Try to remove each supplement and see if coverage is maintained
  for (let i = finalSupplements.length - 1; i >= 0; i--) {
    const supplementToRemove = finalSupplements[i];
    const tempSupplements = finalSupplements.filter(s => s !== supplementToRemove);
    
    // Check if all species are still covered
    const coveredSpecies = new Set();
    tempSupplements.forEach(supplement => {
      const coverage = supplementCoverage[supplement];
      if (coverage) {
        coverage.species.forEach(species => {
          if (missingSpecies.includes(species)) {
            coveredSpecies.add(species);
          }
        });
      }
    });
    
    // If all species are still covered, we can remove this supplement
    if (coveredSpecies.size === missingSpecies.length) {
      finalSupplements.splice(i, 1);
    }
  }
  
  return finalSupplements;
}

// Function to find optimal replacement supplements when FDA supplements are excluded
function findOptimalReplacementSupplements(supplementCoverage, missingSpecies, excludedSupplements, stock) {
  // First, find what species the excluded supplements were covering
  const excludedSpeciesCoverage = new Set();
  const excludedSupplementsCoverage = {};
  
  for (const excludedSupplement of excludedSupplements) {
    if (supplementCoverage[excludedSupplement]) {
      excludedSupplementsCoverage[excludedSupplement] = supplementCoverage[excludedSupplement];
      supplementCoverage[excludedSupplement].species.forEach(species => {
        excludedSpeciesCoverage.add(species);
      });
    }
  }
  
  // Find all available supplements (excluding FDA-selected ones)
  const availableSupplements = Object.entries(supplementCoverage)
    .filter(([supplement]) => !excludedSupplements.includes(supplement))
    .sort(([, a], [, b]) => b.totalScore - a.totalScore);
  
  // Find supplements that can cover the excluded species
  const replacementCandidates = availableSupplements.filter(([supplement, coverage]) => {
    return Array.from(coverage.species).some(species => excludedSpeciesCoverage.has(species));
  });
  
  // Try to find optimal replacement combination
  const selectedReplacements = new Set();
  const coveredExcludedSpecies = new Set();
  
  // Sort candidates by coverage efficiency (considering coverage amounts for excluded species)
  const sortedCandidates = replacementCandidates.sort(([, a], [, b]) => {
    // Calculate total coverage amount for excluded species
    let aTotalAmount = 0;
    let bTotalAmount = 0;
    
    excludedSpeciesCoverage.forEach(species => {
      if (a.species.has(species)) {
        aTotalAmount += a.coverage[species] || 0;
      }
      if (b.species.has(species)) {
        bTotalAmount += b.coverage[species] || 0;
      }
    });
    
    // Prioritize supplements with higher coverage amounts for excluded species
    return bTotalAmount - aTotalAmount;
  });
  
  // Select supplements to cover excluded species with priority based on coverage amounts
  const remainingExcludedSpecies = new Set(excludedSpeciesCoverage);
  
  while (remainingExcludedSpecies.size > 0) {
    let bestSupplement = null;
    let bestScore = 0;
    let bestNewCoverage = [];
    
    // Find the supplement that provides the best coverage for remaining excluded species
    for (const [supplement, coverage] of sortedCandidates) {
      const newCoverage = Array.from(coverage.species)
        .filter(species => remainingExcludedSpecies.has(species));
      
      if (newCoverage.length > 0) {
        // Calculate score based on coverage amounts for remaining excluded species
        let totalAmount = 0;
        newCoverage.forEach(species => {
          totalAmount += coverage.coverage[species] || 0;
        });
        
        // Prioritize supplements with higher coverage amounts
        if (totalAmount > bestScore) {
          bestScore = totalAmount;
          bestSupplement = supplement;
          bestNewCoverage = newCoverage;
        }
      }
    }
    
    if (bestSupplement) {
      selectedReplacements.add(bestSupplement);
      bestNewCoverage.forEach(species => {
        remainingExcludedSpecies.delete(species);
        coveredExcludedSpecies.add(species);
      });
    } else {
      // If no supplement can cover remaining excluded species, break to avoid infinite loop
      break;
    }
  }
  
  // Now find supplements for the remaining species (non-excluded ones)
  const remainingSpecies = missingSpecies.filter(species => !excludedSpeciesCoverage.has(species));
  
  const remainingSupplements = new Set();
  const coveredRemainingSpecies = new Set();
  
  if (remainingSpecies.length > 0) {
    // Sort available supplements by their coverage of remaining species (considering coverage amounts)
    const remainingCandidates = availableSupplements
      .filter(([supplement, coverage]) => {
        return Array.from(coverage.species).some(species => remainingSpecies.includes(species));
      })
      .sort(([, a], [, b]) => {
        // Calculate total coverage amount for remaining species
        let aTotalAmount = 0;
        let bTotalAmount = 0;
        
        remainingSpecies.forEach(species => {
          if (a.species.has(species)) {
            aTotalAmount += a.coverage[species] || 0;
          }
          if (b.species.has(species)) {
            bTotalAmount += b.coverage[species] || 0;
          }
        });
        
        // Prioritize supplements with higher coverage amounts for remaining species
        return bTotalAmount - aTotalAmount;
      });
    
    // Select supplements for remaining species with priority based on coverage amounts
    const remainingSpeciesSet = new Set(remainingSpecies);
    
    while (remainingSpeciesSet.size > 0) {
      let bestSupplement = null;
      let bestScore = 0;
      let bestNewCoverage = [];
      
      // Find the supplement that provides the best coverage for remaining species
      for (const [supplement, coverage] of remainingCandidates) {
        const newCoverage = Array.from(coverage.species)
          .filter(species => remainingSpeciesSet.has(species));
        
        if (newCoverage.length > 0) {
          // Calculate score based on coverage amounts for remaining species
          let totalAmount = 0;
          newCoverage.forEach(species => {
            totalAmount += coverage.coverage[species] || 0;
          });
          
          // Prioritize supplements with higher coverage amounts
          if (totalAmount > bestScore) {
            bestScore = totalAmount;
            bestSupplement = supplement;
            bestNewCoverage = newCoverage;
          }
        }
      }
      
      if (bestSupplement) {
        remainingSupplements.add(bestSupplement);
        bestNewCoverage.forEach(species => {
          remainingSpeciesSet.delete(species);
          coveredRemainingSpecies.add(species);
        });
      } else {
        // If no supplement can cover remaining species, break to avoid infinite loop
        break;
      }
    }
  }
  
  // Combine replacement supplements with remaining supplements
  const finalSupplements = new Set([...selectedReplacements, ...remainingSupplements]);
  
  // Optimize the final combination by removing redundant supplements
  const optimizedSupplements = removeRedundantSupplements(Array.from(finalSupplements), missingSpecies, supplementCoverage);
  
  // Calculate coverage statistics
  const totalCoveredSpecies = new Set();
  optimizedSupplements.forEach(supplement => {
    const coverage = supplementCoverage[supplement];
    if (coverage) {
      coverage.species.forEach(species => {
        if (missingSpecies.includes(species)) {
          totalCoveredSpecies.add(species);
        }
      });
    }
  });
  
  const uncoveredSpecies = missingSpecies.filter(species => !totalCoveredSpecies.has(species));
  const coveragePercentage = Math.round((totalCoveredSpecies.size / missingSpecies.length) * 100);
  
  // console.log('=== SUPPLEMENT SELECTION RESULTS ===');
  // console.log('Selected supplements:', optimizedSupplements);
  // console.log('Coverage:', `${totalCoveredSpecies.size}/${missingSpecies.length} species (${coveragePercentage}%)`);
  // console.log('Lacking species:', uncoveredSpecies);
  
  // Add detailed coverage information for debugging
  // console.log('=== DETAILED COVERAGE ANALYSIS (Replacement) ===');
  optimizedSupplements.forEach(supplement => {
    const coverage = supplementCoverage[supplement];
    if (coverage) {
      // console.log(`${supplement}:`, {
      //   species: Array.from(coverage.species),
      //   coverage: coverage.coverage,
      //   totalScore: coverage.totalScore
      // });
    }
  });
  
  return { 
    supplements: optimizedSupplements, 
    coverage: { 
      covered: totalCoveredSpecies.size, 
      total: missingSpecies.length, 
      percentage: coveragePercentage, 
      uncoveredSpecies: uncoveredSpecies 
    } 
  };
}
